import {BreadcrumbService} from "../../../../services/breadcrumbs/breadcrumb.service";
import {VariantService} from "../../../../services/variant/variant.service";
import {FulfilmentRequestsService} from "../../../../services/fulfilment/fulfilment.service";
import {TicketService} from "../../../../services/tickets/ticket.service";
import {QueryBuilder} from "../../../../services/query-builder.service";
import FulfilmentRequest = InterACT.Interfaces.FulfilmentRequests.FulfilmentRequest;
import FulfilmentRequestResponse = InterACT.Interfaces.FulfilmentRequests.FulfilmentRequestResponse;
import Variant = InterACT.Interfaces.Catalogues.Variant;
import Ticket = InterACT.Interfaces.Tickets.Ticket;
import Action = InterACT.Interfaces.FulfilmentRequests.Action;
import VariantInfo = InterACT.Interfaces.VariantInfo;

const NOT_AVAILABLE = "NOT_AVAILABLE";
const ADD = "ADD";
const DELETE = "DELETE";
const AMEND = "AMEND";
const NEW_CARD = "NEW_CARD";
const REPLACE_CARD = "REPLACE_CARD";
const NAME_USERNAME_PATTERN = "(.+) \\((\\S+)\\)";
const SERVICE_ACCOUNT_PREFIX = "service-account-";
const RAIL_CHANNEL = "Rail (RPS3002)";
const STANDARD_CHANNEL = "Standard";
const RAIL_SYSTEM = "WebTIS";
const RAIL_CLIENT_ID = SERVICE_ACCOUNT_PREFIX + "services";

export class FulfilmentRequestSummaryController {
  public static $inject = [
    '$rootScope',
    'breadcrumbService',
    '$stateParams',
    'fulfilmentRequest',
    'fulfilmentRequestsService',
    'variantService',
    'ticketService',
    'QueryBuilder',
    '$q'
  ];

  private selectedFulfilmentRequest: FulfilmentRequest;
  private variants: Variant[];
  private tickets: Ticket[];
  private chainedVariants: Variant[];
  private variantInfos: VariantInfo[];
  private isrn: string;
  private prodInst: string;
  private allPromises;
  private variantIndexes;
  private ticketsIndexes;
  private chainedVariantIndexes;

  constructor(
    private $rootScope: ng.IRootScopeService,
    private breadcrumbService: BreadcrumbService,
    private $stateParams: ng.ui.IStateParamsService,
    private fulfilmentRequestResponse: FulfilmentRequestResponse,
    private fulfilmentRequestsService: FulfilmentRequestsService,
    private variantService: VariantService,
    private ticketService: TicketService,
    private queryBuilderService: QueryBuilder,
    private $q: ng.IQService
  ) {
    this.variants = [];
    this.tickets = [];
    this.chainedVariants = [];
    this.variantInfos = [];
    this.allPromises = [];
    this.variantIndexes = [];
    this.ticketsIndexes = [];
    this.chainedVariantIndexes = [];
    this.activate();
  }

  /**
   * Gets Fulfilment's Request State
   *
   * @param fulfilmentRequest object, has information like scheme, target, actions.
   *
   * @returns Fulfilment's Request state
   */
  private getState(fulfilmentRequest: FulfilmentRequest) {
    return this.fulfilmentRequestsService.getFulfilmentRequestState(fulfilmentRequest);
  }

  /**
   * - Creates Variant promises and stores them in allPromises.
   * - In variantIndexes array stores the index of the new promise that is inserted in allPromises array.
   *   Saving the indexes of Variant type promises in the allPromises array, will be used afterwords to
   *   identify the variant type responses in q.all() method when resolving all promises.
   *
   * @param variantExternalId : string which consists of catalogueId, productId, variantId
   */
  private setVariantPromises(variantExternalId: string) {
    this.variantIndexes.push(this.allPromises.push(this.getCatalogueVariant(variantExternalId)) - 1);
  }

  /**
   * - Creates Ticket promises and stores them in allPromises.
   * - In ticketsIndexes array stores the index of the new promise that is inserted in allPromises array.
   *   Saving the indexes of Ticket type promises in the allPromises array, will be used afterwords to
   *   identify the ticket responses in q.all() method when resolving all promises.
   * - Ticket service is expected to return one ticket object inside the ticket's array
   *   while searching with versionReference.
   *
   * @param reference : string attribute equal to target.reference of a Fulfilment Request in case of AMEND action
   *                                     equal to action.productInstanceExternalId of Fulfilment Request in case of DELETE action
   *                                     equal to versionReference of Ticket Request
   * @returns Ticket promise
   */
  private setTicketPromises(reference: string) {
    const fieldName = 'versionReference';
    const queryValue = reference;
    const query = this.queryBuilderService.fieldIs(fieldName, queryValue).getParam();
    const ticketPromise = this.ticketService.getTickets(query);
    this.ticketsIndexes.push(this.allPromises.push(ticketPromise) - 1);
    return ticketPromise;
  }

  /**
   * - Chains a Variant promise after Ticket promise.
   * - The VariantExternalID is extracted from ticket's catalogueItem.link attribute and subsequently is used
   *   to call the catalogue service.
   * - In chainedVariantIndexes array stores the index of the new promise that is inserted in allPromises array.
   *   Saving the indexes of "chained" Variant promises in the allPromises array, will be used afterwords to
   *   identify the "chained" Variant responses in q.all() method when resolving all promises.
   * - Storing the "chained" promise in a variable lets the q.all() method to resolve it and place its resolution
   *   in the response array.
   *
   *  Error Handling:
   *  - If the catalogueItem.link attribute is missing or is empty,
   *    the promise is rejected and HTTP request is not sent.
   *  - If the getCatalogueVariant HTTP request is sent and
   *    is unsuccessful, then the promise is rejected also.
   *  The catch of the rejected promises is in the getServiceResponses.
   *
   * @param promise: IPromise is the promise from ticket service
   *
   * @returns Variant promise or throws an Error if link is missing
   */
  private chainVariantPromise(promise: angular.IPromise<Array<Ticket>>) {
    const chainedPromise = promise.then(  (ticket) => {
      if (!_.isEmpty(ticket[0].catalogueItem) && !_.isEmpty(ticket[0].catalogueItem.link)) {
        const variantExternalId = this.convertLinkToVariantExternalId(ticket[0].catalogueItem.link);
        return this.getCatalogueVariant(variantExternalId);
      }
      else {
        throw new Error('CatalogueItem.link attribute is missing.');
      }
    });
    this.chainedVariantIndexes.push(this.allPromises.push(chainedPromise) - 1);
  }

  /**
   * Sets promises
   * Iterates over the Actions array of a Fulfilment Request.
   */
  private setPromises() {
    if (!_.isEmpty(this.selectedFulfilmentRequest.actions)) {
      for (let action of this.selectedFulfilmentRequest.actions) {
        if (action.type === ADD) {
          if (!_.isEmpty(action.variantExternalId)) {
            this.setVariantPromises(action.variantExternalId);
          }
        } else if (action.type === AMEND) {
          if (!_.isEmpty(this.selectedFulfilmentRequest.target) &&
            !_.isEmpty(this.selectedFulfilmentRequest.target.reference)) {
            const ticketPromise = this.setTicketPromises(this.selectedFulfilmentRequest.target.reference);
            this.chainVariantPromise(ticketPromise);
          }
        } else if (action.type === DELETE) {
          if (!_.isEmpty(action.productInstanceExternalId)) {
            const ticketPromise = this.setTicketPromises(action.productInstanceExternalId);
            this.chainVariantPromise(ticketPromise);
          }
        }
      }// END OF LOOP
    }
  }

  /**
   * Converts the catalogueItem.link attribute of the ticket object into the format
   * of VariantExternalID which consists of the CatalogueId, productId, variantId.
   *
   * @param link : string with the value of catalogueItem.link
   *
   * @returns string in the format catalogueId productId variantId
   */
  private convertLinkToVariantExternalId(link: string) {
    const variantExternalID = link.substring(1, link.indexOf("?"));
    return variantExternalID;
  }

  /**
   * Creates an object with attributes CatalogueId, productId and variantId
   *
   * @param variantExternalId : string which consists of catalogueId, productId, variantId
   *
   * @returns Object with variantExternalId elements
   */
  private splitVariantExternalId = (variantExternalId: string) => {
    const splitVariantLink = variantExternalId.split('/');
    return {
      catalogueId: splitVariantLink[1],
      productId: splitVariantLink[3],
      variantId: splitVariantLink[5]
    };
  }

  /**
   * Gets variant
   *
   * @param variantExternalId : string which consists of catalogueId, productId, variantId
   *
   * @returns promise of Variant
   */
  private getCatalogueVariant = (variantExternalId: string) => {
    let constituents = this.splitVariantExternalId(variantExternalId);
    return this.variantService.getVariant(constituents.catalogueId,
                                          parseInt(constituents.productId),
                                          parseInt(constituents.variantId));
  }

  /**
   * Retrieves the Channel value
   *
   * @returns the channel for the fulfilment request
   */
  private getChannel() {
    if (this.isRailRequest()) {
      return RAIL_CHANNEL;
    } else {
      return STANDARD_CHANNEL;
    }
  }

  /**
   * Retrieves System value
   *
   * @returns the system for the fulfilment request
   */
  private getSystem() {
    if (this.isRailRequest()) {
      return RAIL_SYSTEM;
    } else {
      return this.getClientName()
    }
  }

  /**
   * Checks if the fulfilment request was created by a rail client
   *
   * @returns true if the fulfilment request was created by a rail client
   */
  private isRailRequest() {
    return this.fulfilmentRequestResponse.meta.createdBy.includes(RAIL_CLIENT_ID);
  }

  /**
   * Determines the client name from the user who created the fulfilment request
   *
   * @returns the client name
   */
  private getClientName() {
    let user = this.getUser();
    if (user.startsWith(SERVICE_ACCOUNT_PREFIX)) {
      return user.replace(SERVICE_ACCOUNT_PREFIX, "");
    } else {
      return user;
    }
  }

  /**
   * Extracts the user who created the fulfilment request from meta.createdBy
   *
   * The format of meta.createdBy is either 'name (preferredUsername)' or 'preferredUsername'
   *
   * For a non service account:
   *    name = user.firstName user.lastName
   *    preferredUsername = user.username
   *
   * For a service account
   *    name = null
   *    preferredUsername = client.clientId
   *
   * @returns the user who created the fulfilment request
   */
  private getUser() {
    if (_.isEmpty(this.fulfilmentRequestResponse.meta.createdBy)) {
      return NOT_AVAILABLE;
    } else {
      let client = this.fulfilmentRequestResponse.meta.createdBy.match(NAME_USERNAME_PATTERN);

      if (!_.isEmpty(client)) {
        if (client[1] === "null") {
          return client[2];
        } else {
          return client[1];
        }
      } else {
        return this.fulfilmentRequestResponse.meta.createdBy;
      }
    }
  }

  /**
   * Assigns the data into collections
   * These data will be depicted in the view
   *
   */
  private prepareDataForDisplay() {
    this.utilizeTarget();
    this.utilizeActions();
  }

  /**
   * - Handles ADD actions.
   * - Populates the VariantInfo instances to be depicted in the view.
   *
   * example in case of two ADD actions :
   *  1) ADD : variants[0] = variantObject
   *  2) ADD : variants[1] = variantObject
   *
   * The shift() method removes the first element from the array and returns that removed element.
   *
   * @param action : Action instance
   */
  private setDisplayDataForAddAction(action: Action) {
    if (!_.isEmpty(action.variantExternalId)) {
      const constituents = this.splitVariantExternalId(action.variantExternalId);
      const variantInfo = this.variants.shift();
      if (!_.isEmpty(variantInfo)) {
        this.variantInfos.push({
          actionType: !_.isEmpty(action.type) ? action.type : NOT_AVAILABLE,
          name: !_.isEmpty(variantInfo.name) ? variantInfo.name : NOT_AVAILABLE,
          description: !_.isEmpty(variantInfo.description) ? variantInfo.description : NOT_AVAILABLE,
          catalogueId: constituents.catalogueId,
          productId: constituents.productId,
          variantId: constituents.variantId,
          productInstance: !_.isEmpty(this.prodInst) ? this.prodInst : NOT_AVAILABLE
        });
      }
      // The variant is empty
      else {
        this.variantInfos.push({
          actionType: !_.isEmpty(action.type) ? action.type : NOT_AVAILABLE,
          name: NOT_AVAILABLE,
          description: NOT_AVAILABLE,
          catalogueId: constituents.catalogueId,
          productId: constituents.productId,
          variantId: constituents.variantId,
          productInstance: !_.isEmpty(this.prodInst) ? this.prodInst : NOT_AVAILABLE
        });
      }
    }
    // The variantExternalId is empty
    else {
      this.variantInfos.push({
        actionType: !_.isEmpty(action.type) ? action.type : NOT_AVAILABLE,
        name: NOT_AVAILABLE,
        description: NOT_AVAILABLE,
        catalogueId: NOT_AVAILABLE,
        productId: NOT_AVAILABLE,
        variantId: NOT_AVAILABLE,
        productInstance: !_.isEmpty(this.prodInst) ? this.prodInst : NOT_AVAILABLE
      });
    }
  }

  /**
   * - Handles the AMEND and DELETE actions.
   * - Populates the VariantInfo instances to be depicted in the view.
   *
   * The tickets and the chainedVariants arrays store in corresponding positions the related Ticket and Variant objects.
   * If the corresponding Variant object is missing the chainedVariants position will have undefined value.
   *
   * example in case of DELETE action :
   *  1) DELETE : tickets[0] = ticketObject , chainedVariants[0] = variantObject
   *
   * The shift() method removes the first element from the array and returns that removed element.
   *
   * @param action : Action instance
   */
  private setDisplayDataForAmendDeleteActions(action: Action) {
    if (this.tickets !== undefined && this.tickets.length != 0) {
       const ticketInfo = this.tickets.shift();
       const chainedVariantInfo = this.chainedVariants.shift();
      if (!_.isEmpty(ticketInfo.catalogueItem)) {
        const variantExternalID = this.convertLinkToVariantExternalId(ticketInfo.catalogueItem.link);
        const constituents = this.splitVariantExternalId(variantExternalID);
        if (chainedVariantInfo !== undefined) {
          this.variantInfos.push({
            actionType: !_.isEmpty(action.type) ? action.type : NOT_AVAILABLE,
            name: !_.isEmpty(ticketInfo.catalogueItem.name) ? ticketInfo.catalogueItem.name : NOT_AVAILABLE,
            description: !_.isEmpty(chainedVariantInfo.description) ? chainedVariantInfo.description : NOT_AVAILABLE,
            catalogueId: constituents.catalogueId,
            productId: constituents.productId,
            variantId: constituents.variantId,
            productInstance: !_.isEmpty(this.prodInst) ? this.prodInst : NOT_AVAILABLE
          });
        }
        // The description is empty
        else {
          this.variantInfos.push({
            actionType: !_.isEmpty(action.type) ? action.type : NOT_AVAILABLE,
            name: !_.isEmpty(ticketInfo.catalogueItem.name) ? ticketInfo.catalogueItem.name : NOT_AVAILABLE,
            description: NOT_AVAILABLE,
            catalogueId: constituents.catalogueId,
            productId: constituents.productId,
            variantId: constituents.variantId,
            productInstance: !_.isEmpty(this.prodInst) ? this.prodInst : NOT_AVAILABLE
          });
        }
      }
      // The catalogueItem is empty
      else {
        this.variantInfos.push({
          actionType: !_.isEmpty(action.type) ? action.type : NOT_AVAILABLE,
          name: NOT_AVAILABLE,
          description: NOT_AVAILABLE,
          catalogueId: NOT_AVAILABLE,
          productId: NOT_AVAILABLE,
          variantId: NOT_AVAILABLE,
          productInstance: !_.isEmpty(this.prodInst) ? this.prodInst : NOT_AVAILABLE
        });
      }
    }
    // The tickets array is empty
    else {
      this.variantInfos.push({
        actionType: !_.isEmpty(action.type) ? action.type : NOT_AVAILABLE,
        name: NOT_AVAILABLE,
        description: NOT_AVAILABLE,
        catalogueId: NOT_AVAILABLE,
        productId: NOT_AVAILABLE,
        variantId: NOT_AVAILABLE,
        productInstance: !_.isEmpty(this.prodInst) ? this.prodInst : NOT_AVAILABLE
      });
    }
  }

  /**
   * Handles NEW/REPLACE_CARD actions.
   *
   * @param action : Action instance
   */
  private setDisplayDataForCardAction(action: Action) {
    let description;
    if ( action.type === REPLACE_CARD ) {
      description = "CARD_ISSUANCE.REPLACE_CARD_DESCRIPTION"
    } else {
      description = "CARD_ISSUANCE.NEW_CARD_DESCRIPTION"
    }

    this.variantInfos.push({
      actionType: action.type,
      name: action.attributes.CardType,
      description: description,
      catalogueId: NOT_AVAILABLE,
      productId: NOT_AVAILABLE,
      variantId: NOT_AVAILABLE,
      productInstance: NOT_AVAILABLE
    });
  }

  /**
   * - Populates the VariantInfo instances to be depicted in the view for all action types.
   * - Checks if the Fulfilment's Request Actions exist.
   * - Iterates over the actions and calls the corresponding methods.
   * - Handles cases that the attributes are empty.
   */
  private utilizeActions() {
    if (!_.isEmpty(this.selectedFulfilmentRequest.actions)) {
      this.selectedFulfilmentRequest.actions.forEach((action) => {
        if (!_.isEmpty(action)) {
          if (!_.isEmpty(action.productInstanceExternalId)) {
            if (action.type === ADD || action.type === DELETE) {
              this.prodInst = action.productInstanceExternalId;
            }
          }
          if (action.type === ADD) {
            this.setDisplayDataForAddAction(action);
          }
          if (action.type === AMEND || action.type === DELETE) {
            this.setDisplayDataForAmendDeleteActions(action);
          }
          if (action.type === NEW_CARD || action.type === REPLACE_CARD )
          {
            this.setDisplayDataForCardAction(action);
          }
          this.prodInst = NOT_AVAILABLE;
        }
        // The action object is empty
        else {
          this.variantInfos.push({
            actionType: NOT_AVAILABLE,
            name: NOT_AVAILABLE,
            description: NOT_AVAILABLE,
            catalogueId: NOT_AVAILABLE,
            productId: NOT_AVAILABLE,
            variantId: NOT_AVAILABLE,
            productInstance: !_.isEmpty(this.prodInst) ? this.prodInst : NOT_AVAILABLE
          });
        }
      }); // END OF LOOP
    }
    // The actions array is empty
    else {
      this.variantInfos.push({
        actionType: NOT_AVAILABLE,
        name: NOT_AVAILABLE,
        description: NOT_AVAILABLE,
        catalogueId: NOT_AVAILABLE,
        productId: NOT_AVAILABLE,
        variantId: NOT_AVAILABLE,
        productInstance: !_.isEmpty(this.prodInst) ? this.prodInst : NOT_AVAILABLE
      });
    }
  }

  /**
   *  - Checks if the Fulfilment's Request Target exists.
   *  - Assign the Target's fields that will be depicted in the view.
   */
  private utilizeTarget() {
    if (!_.isEmpty(this.selectedFulfilmentRequest.target)) {
      if (this.selectedFulfilmentRequest.target.type === "CUSTOMER_MEDIA") {
        this.isrn = this.selectedFulfilmentRequest.target.reference;
      }
      if (this.selectedFulfilmentRequest.target.type === "PRODUCT_INSTANCE") {
        this.prodInst = this.selectedFulfilmentRequest.target.reference;
      }
    }
  }

  /**
   * - Get all data from Services.
   * - If a promise is rejected it assigns undefined value as response.
   * - The .all() method preserves the order of the resolved values:
   *   So the allPromises[0] will have the resolution in response[0] etc.
   *
   * - Example in case of three actions = [DELETE, ADD, ADD]  in the Fulfilment Request:
   *   The allPromises will be :              The Response will be:
   *   [ticketPromise,                        [ticketObject,
   *   "chained" VariantPromise,              "chained" VariantObject,
   *   VariantPromise,                         VariantObject,
   *   VariantPromise]                         VariantObject]
   *
   *   To identify the responses' positions uses indexes:
   *   variantIndexes = [2,3], ticketsIndexes = [0], chainedVariants = [1]
   *
   *  - Based on the indexes store the objects in the corresponding arrays
   *  For ADD:                               For DELETE:
   *  variants[0] = VariantObject            tickets[0] = ticketObject  chainedVariants[0] = VariantObject
   *  variants[1] = VariantObject
   *
   */
  private getServiceResponses() {
    this.$q.all(this.allPromises.map(p => p.catch(() => undefined))).then(responses => {
      this.variantIndexes.forEach((promiseIndex) => {
        this.variants.push(<Variant>responses[promiseIndex]);
      });
      // Ticket service returns an array with one ticket
      this.ticketsIndexes.forEach((promiseIndex) => {
        if (responses[promiseIndex][0] !== undefined && responses[promiseIndex][0].length != 0) {
          this.tickets.push(<Ticket>responses[promiseIndex][0]);
        }
      });
      this.chainedVariantIndexes.forEach((promiseIndex) => {
        this.chainedVariants.push(<Variant>responses[promiseIndex]);
      });
      this.prepareDataForDisplay();
      this.$rootScope.showSpinner = false;
    })
  }

  /**
   * Gets invoked upon instantiation
   * Set promises
   * Get Responses
   */
  private activate = () => {
    this.$rootScope.showSpinner = true;
    this.isrn = NOT_AVAILABLE;
    this.prodInst = NOT_AVAILABLE;

    this.selectedFulfilmentRequest = this.fulfilmentRequestResponse.fulfilmentRequest;

    this.setPromises();
    this.getServiceResponses();

    this.breadcrumbService.setBreadcrumbs([{
      name: 'FULFILMENT',
      sref: 'fulfilment'
    }, {
      name: 'FULFILMENT_REQUESTS',
      sref: 'fulfilment-requests',
    }, {
      name: this.$stateParams.externalId,
      sref: 'fulfilment-request-details'
    }])
  }
}
